import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Launch } from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getCIDS } from "../services/cidService";
import { globalSearch } from "../services/searchService";
import { getDefinitions } from "../services/settingsService";
import { setDefinitions } from "../features/settingsSlice";
import {
  loadCIDIntoAssessor,
  loadPropertyIntoAssessor,
} from "../services/assessorLoad";
import { notifyService } from "../services/notifyService";
import ContactEditDialog from "./contactEditDialog";
import CreateContact from "./createContact";
import { canWriteAnyCid } from "../utils/permissions";

const EMPTY_RESULTS = { contacts: [], properties: [] };

const Search = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const settingsLoaded = useSelector((state) => state.settings.value.loaded);
  const auth = useSelector((state) => state.auth.value);
  const contactsWritable = canWriteAnyCid(auth);

  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [cidFilter, setCidFilter] = useState("");
  const [cids, setCids] = useState([]);
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    getCIDS().then((res) => {
      if (res) {
        setCids(res);
      }
    });
  }, []);

  useEffect(() => {
    if (!settingsLoaded) {
      getDefinitions().then((defs) => {
        if (defs) {
          dispatch(setDefinitions(defs));
        }
      });
    }
  }, [settingsLoaded, dispatch]);

  const runSearch = useCallback(async () => {
    if (!q.trim() && !cidFilter) {
      setResults(EMPTY_RESULTS);
      setSearched(false);
      return;
    }
    setLoading(true);
    const res = await globalSearch({
      q: q.trim(),
      types: typeFilter === "all" ? "contacts,properties" : typeFilter,
      cid: cidFilter,
    });
    setResults(res || EMPTY_RESULTS);
    setSearched(true);
    setLoading(false);
  }, [q, typeFilter, cidFilter]);

  useEffect(() => {
    const timer = setTimeout(runSearch, 300);
    return () => clearTimeout(timer);
  }, [runSearch]);

  const showContacts = typeFilter !== "properties";
  const showProperties = typeFilter !== "contacts";

  const openInQuickAssessor = async (property) => {
    setOpening(property.id);
    try {
      navigate("/app/quickAssessor");
      await loadCIDIntoAssessor(dispatch, property.cid, cids);
      await loadPropertyIntoAssessor(dispatch, property.id);
    } catch (err) {
      console.log(err);
      notifyService("Failed to open property in Quick Assessor");
    } finally {
      setOpening(null);
    }
  };

  return (
    <Grid container spacing={2} sx={{ p: { xs: 1, md: 2 } }}>
      <ContactEditDialog
        open={Boolean(editContact)}
        contact={editContact}
        onClose={() => setEditContact(null)}
        onChanged={runSearch}
      />
      <CreateContact
        dialogOpen={createOpen}
        handleClose={setCreateOpen}
        onCreated={async (created) => {
          if (created?.name) {
            setQ(created.name);
            setTypeFilter("all");
          }
          await runSearch();
        }}
      />

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1.5}
              >
                <Box>
                  <Typography variant="h5">Search</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Find contacts and properties across every CID you can access.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  disabled={!contactsWritable}
                  onClick={() => setCreateOpen(true)}
                >
                  Create Contact
                </Button>
              </Stack>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <TextField
                  autoFocus
                  fullWidth
                  label="Search"
                  placeholder="Name, company, phone, email, parcel ID, address, owner..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <ToggleButtonGroup
                  exclusive
                  color="primary"
                  value={typeFilter}
                  onChange={(e, value) => value && setTypeFilter(value)}
                  size="small"
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="contacts">Contacts</ToggleButton>
                  <ToggleButton value="properties">Properties</ToggleButton>
                </ToggleButtonGroup>
                {showProperties ? (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>CID</InputLabel>
                    <Select
                      label="CID"
                      value={cidFilter}
                      onChange={(e) => setCidFilter(e.target.value)}
                    >
                      <MenuItem value="">All CIDs</MenuItem>
                      {cids.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
                {loading ? <CircularProgress size={22} /> : null}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {showProperties ? (
        <Grid item xs={12} lg={showContacts ? 7 : 12}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Properties</Typography>
                <Chip size="small" label={results.properties.length} />
              </Stack>
              {results.properties.length ? (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Parcel ID</TableCell>
                        <TableCell>CID</TableCell>
                        <TableCell>Owner</TableCell>
                        <TableCell>Building Address</TableCell>
                        <TableCell align="right">Assessed</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.properties.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{p.parcelID}</TableCell>
                          <TableCell>{p.cids?.name || "—"}</TableCell>
                          <TableCell>{p.owner_name}</TableCell>
                          <TableCell>{p.building_address}</TableCell>
                          <TableCell align="right">
                            {p.assessor_value != null && p.assessor_value !== ""
                              ? `$${Number(p.assessor_value).toLocaleString()}`
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                p.cid
                                  ? "Open in Quick Assessor"
                                  : "Property has no CID"
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  endIcon={<Launch />}
                                  disabled={!p.cid || opening === p.id}
                                  onClick={() => openInQuickAssessor(p)}
                                >
                                  Open
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {searched
                    ? "No matching properties."
                    : "Type to search parcel IDs, addresses, owners — or pick a CID."}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ) : null}

      {showContacts ? (
        <Grid item xs={12} lg={showProperties ? 5 : 12}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Contacts</Typography>
                <Chip size="small" label={results.contacts.length} />
              </Stack>
              {results.contacts.length ? (
                <List disablePadding>
                  {results.contacts.map((contact) => (
                    <ListItemButton
                      key={contact.id}
                      divider
                      onClick={() => setEditContact(contact)}
                    >
                      <ListItemText
                        primary={contact.name || "Unnamed Contact"}
                        secondary={[contact.company, contact.phone_number, contact.email]
                          .filter(Boolean)
                          .join(" • ")}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {searched
                    ? "No matching contacts."
                    : "Type to search names, companies, phones, emails."}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default Search;
